<template>
  <StellaNavToolbar>
    <template #left>
      <StellaMenu alignment="start" @open="trackOpen('new')">
        <template #default="{ isOpened }">
          <StellaButton
            type="ghost"
            :iconRight="isOpened ? 'chevron-up' : 'chevron-down'"
            size="sm"
            :active="isOpened"
            class="brand-navigation:text-navigationText"
          >
            New
          </StellaButton>
        </template>
        <template #content>
          <StellaMenuList :items="createNavMenuListItems" />
        </template>
      </StellaMenu>
      <StellaMenu alignment="start" @open="trackOpen('open')">
        <template #default="{ isOpened }">
          <StellaButton
            type="ghost"
            :iconRight="isOpened ? 'chevron-up' : 'chevron-down'"
            size="sm"
            :active="isOpened"
            class="brand-navigation:text-navigationText"
          >
            Open
          </StellaButton>
        </template>
        <template #content>
          <StellaMenuList :items="openNavMenuListItems" />
        </template>
      </StellaMenu>
    </template>

    <template #right>
      <div class="flex items-center h-full space-x-1">
        <template v-if="!loadingDesigner && designer">
          <StellaLink
            v-if="hasBrandAccounts && device.isAtLeastMd"
            to="https://brand.spoak.com"
          >
            <StellaButton type="tertiary" size="sm">
              Go to Brand Portal
            </StellaButton>
          </StellaLink>

          <StellaTooltip text="Kudos">
            <StellaButton
              type="ghost"
              size="sm"
              class="!sm<:px-2"
              @click="openKudos"
              v-track="'Kudos Clicked'"
              v-track-props="{ component: 'Top Nav' }"
            >
              <StellaIcon icon="feedback" size="md" />
            </StellaButton>
          </StellaTooltip>

          <DailyPlayWidget />

          <StellaLink
            to="https://help.spoak.com/"
            newWindow
            class="border-grey-20 hidden md:flex"
          >
            <StellaTooltip text="Get Help" position="bottom">
              <StellaButton
                type="ghost"
                size="sm"
                class="!sm:<px-2"
                @click="getHelpClicked"
              >
                <StellaIcon
                  icon="questionmark-circle"
                  size="md"
                  v-track:mousedown="'Get Help Clicked'"
                  v-track-props="{ component: 'Top Nav' }"
                />
              </StellaButton>
            </StellaTooltip>
          </StellaLink>

          <StellaMenu v-if="!device.isMd" alignment="end">
            <template #default="{ isOpened }">
              <StellaButton
                type="ghost"
                :iconRight="isOpened ? 'chevron-up' : 'chevron-down'"
                size="sm"
                :active="!device.isMd && isOpened"
              >
                Account
              </StellaButton>
            </template>
            <template #content>
              <StellaMenuList :items="accountNavMenuListItems" />
            </template>
          </StellaMenu>

          <StellaMenu v-else alignment="end" position="bottom">
            <template #default="{ isOpened }">
              <StellaTooltip>
                <StellaButton
                  type="ghost"
                  size="sm"
                  class="!sm<:px-2"
                  :active="isOpened"
                >
                  <StellaIcon
                    icon="contactcard"
                    size="md"
                    v-track:mousedown="'Account Clicked'"
                    v-track-props="{ component: 'Top Nav' }"
                  />
                </StellaButton>
              </StellaTooltip>
            </template>
            <template #content>
              <StellaMenuList :items="accountNavMenuListItems" />
            </template>
          </StellaMenu>
        </template>
        <template v-else-if="!loadingDesigner && !designer">
          <StellaLink
            :to="{ name: 'signup' }"
            class="border-grey-20"
            v-track="'Join Spoak Clicked'"
            v-track-props="{ component: 'Top Nav' }"
          >
            <StellaButton type="secondary" size="sm">Join</StellaButton>
          </StellaLink>
        </template>
      </div>
    </template>
  </StellaNavToolbar>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue'
import { type StellaMenuListItem } from '/@/stella/StellaMenu'
import { useTracker } from '/@/core/tracking/api'
import { useRouter } from 'vue-router'
import { usePermission } from '/@/common/tiers'
import { useUser } from '/@/data/user'
import { useCurrentDesigner } from '/@/data/designer/current'
import { useConfig } from '/@/core/config'
import { useLoading } from '/@/core/elements/loading'
import { useToast } from '/@/core/elements/toast'
import { useDesignerApi } from '/@/data/designer/api'
import { device } from '/@/utils/responsive'
import { useToolbarUtils } from './utils'
import { useAccountsData } from '/@/common/accounts/api'
import { useInternalSignalApi } from '/@/common/internalSignal/api'
import DailyPlayWidget from '/@/common/toolbar/Stella/DailyPlayWidget.vue'
import { stellaModal } from '/@/stella/StellaModal'

const user = useUser()
const {
  designer,
  isPaying,
  isOrgMember,
  loading: loadingDesigner,
} = useCurrentDesigner()
const { membershipSubscription, teamMembers, canEditTeam, hasBrandAccounts } =
  useAccountsData()

const designerApi = useDesignerApi()
const tracker = useTracker()
const router = useRouter()
const loading = useLoading()
const toast = useToast()

const internalSignalApi = useInternalSignalApi()

const config = useConfig()
const referralPermission = usePermission('REFERRAL_LINKS')
const toolbarUtils = useToolbarUtils('Top Nav')

const openNavMenuListItems = computed<StellaMenuListItem[]>(() => {
  const items: StellaMenuListItem[] = []

  items.push(...toolbarUtils.openMenuListItems())

  return items
})

const createNavMenuListItems = computed<StellaMenuListItem[]>(() => {
  const items: StellaMenuListItem[] = []

  items.push(...toolbarUtils.createMenuListItems())

  return items
})

const accountNavMenuListItems = computed<StellaMenuListItem[]>(() => {
  const items: StellaMenuListItem[] = []

  const designerValue = designer.value
  const userValue = user.current

  if (designerValue) {
    if (!isOrgMember.value) {
      items.push(
        {
          type: 'item',
          id: 'myprofile',
          label: 'View profile',
          action: () => {
            tracker.send('My Profile Clicked', { component: 'Top Nav' })
            router.push({
              name: 'profile',
              params: { nickname: designerValue.nickname },
            })
            return false
          },
        },
        {
          type: 'item',
          id: 'settings',
          label: 'Edit profile',
          action: () => {
            tracker.send('Edit Profile Clicked', { component: 'Top Nav' })
            router.push({
              name: 'settings',
            })
            return false
          },
        },
        { type: 'divider' },
        {
          type: 'item',
          id: 'subscription',
          label: 'Billing & subscription',
          action: () => {
            tracker.send('Subscription Clicked', {
              compnent: 'Top Nav',
            })
            router.push({
              name: 'settings',
              query: {
                t: 'subscription',
              },
            })
          },
        },
      )
    }

    if (
      canEditTeam.value ||
      teamMembers.value.length > 1 ||
      (!isOrgMember.value &&
        isPaying.value &&
        designerValue.tier !== 'INDIVIDUAL_1' &&
        designerValue.tier !== 'INDIVIDUAL_1_1' &&
        designerValue.tier !== 'PLAY')
    ) {
      items.push({
        type: 'item',
        id: 'team',
        label: 'Team details',
        icon:
          canEditTeam.value || teamMembers.value.length > 1
            ? undefined
            : 'premium',
        iconClass: 'text-purple-50',
        action: () => {
          tracker.send('Team Clicked', {
            compnent: 'Top Nav',
          })
          router.push({
            name: 'settings',
            query: {
              t: 'team',
            },
          })
        },
      })
    }

    if (items.length > 0) items.push({ type: 'divider' })

    if (designer.value.stripeCustomerId) {
      items.push({
        type: 'item',
        id: 'invite',
        label: 'Refer a friend, get $10',
        action() {
          tracker.send('Invite A Friend Clicked', {
            component: 'Top Nav',
          })
          referralPermission.tierGuard(
            () => {
              router.push({ name: 'settings-referral' })
            },
            {
              source: 'Top Nav',
            },
          )
        },
      })
      items.push({ type: 'divider' })
    }
  }

  if (userValue) {
    items.push({
      type: 'item',
      id: 'logout',
      label: 'Log out',
      action: () => {
        tracker.send('Logout Clicked', {
          component: 'Top Nav',
        })
        window.location.href = `${config.urls.admin}/logout`
        return false
      },
    })
  }

  if (
    designerValue &&
    designerValue?.tier !== 'FREE' &&
    membershipSubscription.value?.cancelAt
  ) {
    items.push({ type: 'divider' })
    items.push({
      type: 'item',
      id: 'keep',
      label: 'Keep membership',
      action() {
        tracker.send('Keep Membership Clicked', {
          component: 'Top Nav',
        })
        stopCancelation()
      },
    })
  }

  return items
})

async function stopCancelation() {
  loading.show()
  await designerApi.stopCancelation()
  loading.hide()
  toast.show({
    type: 'stella',
    message: 'Yay! Welcome back. Your cancelation has been…canceled.',
    severity: 'success',
  })
}

function trackOpen(type: 'new' | 'open') {
  tracker.send('File Menu Opened', {
    component: 'Top Nav',
    type,
  })
}

function openKudos() {
  stellaModal.open({
    content: defineAsyncComponent(
      () => import('/@/stella/modals/KudosModalContent.vue'),
    ),
  })
}

async function getHelpClicked() {
  await internalSignalApi.trackEvent({ eventType: 'HELP_HUB_CLICKED' })
}
</script>

<style scoped lang="scss"></style>
